import React, { useRef } from 'react';
import {
  pickLocalized,
  computeCompletedAbilitiesProgress,
  getPaperProgress
} from '../../utils/testUtils';
import {
  CORE_ABILITY_SLUGS,
  CORE_ABILITY_COUNT,
  normalizeAbilitySlug
} from '../../data/abilityPapers';
import PageTutorialGuide from '../tutorial/PageTutorialGuide';
import TutorialInlineButton from '../tutorial/TutorialInlineButton';
import { resolveTutorialGuideSteps } from '../tutorial/tutorialGuideSteps';
import { usePageTutorial } from '../tutorial/usePageTutorial';
import AbilityInfographic from './AbilityInfographic';
import { getAbilityTheme } from '../../utils/abilityTheme';
import AlgorithmResultsDisclaimer from './AlgorithmResultsDisclaimer';
import {
  testProgressTrack,
  testProgressFill,
  testFocusRing
} from './testUiClasses';

const UNIFORM_GRID_ROW_HEIGHT = 'minmax(168px, 1fr)';

function AbilityPaperMenu({
  t,
  lang,
  papers,
  completedSlugs,
  progressBySlug,
  onSelectPaper,
  autoOpenTutorial = false,
  embedded = false,
}) {
  const { guideOpen, openGuide, closeGuide } = usePageTutorial('ability', { forceOpen: autoOpenTutorial });
  const guideSteps = resolveTutorialGuideSteps('ability', t);
  const progressRef = useRef(null);
  const gridRef = useRef(null);
  const firstTileRef = useRef(null);

  const { completed, total } = computeCompletedAbilitiesProgress(
    completedSlugs,
    CORE_ABILITY_SLUGS
  );
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

  const anchorRefs = {
    progress: progressRef,
    grid: gridRef,
    firstTile: firstTileRef
  };

  const renderPaperTile = (paper, index, { compact = false, tileRef } = {}) => {
    const slug = normalizeAbilitySlug(paper.slug);
    const isComplete = completedSlugs?.has?.(slug);
    const isSoon = Boolean(paper.comingSoon);
    const { answered, total: questionTotal } = getPaperProgress(paper, progressBySlug);
    const isInProgress = !isComplete && !isSoon && answered > 0;
    const title = pickLocalized(paper.title, lang);

    return (
      <AbilityPaperTile
        key={slug}
        tileRef={tileRef}
        index={index}
        infographicVariant={slug}
        title={title}
        isComplete={isComplete}
        isSoon={isSoon}
        isInProgress={isInProgress}
        answered={answered}
        questionTotal={questionTotal}
        partsCompleted={completed}
        partsTotal={CORE_ABILITY_COUNT}
        hideIdleBadge={compact}
        testFocusRing={testFocusRing}
        t={t}
        onOpenPart={() => onSelectPaper(paper)}
        compact={compact}
      />
    );
  };

  if (embedded) {
    return (
      <div className="px-2 py-2">
        <div
          className="grid w-full grid-cols-2 gap-2 items-stretch"
          style={{ gridAutoRows: 'minmax(100px, auto)' }}
        >
          {papers.map((paper, index) => {
            const isLastOddTile = papers.length % 2 !== 0 && index === papers.length - 1;
            const tile = renderPaperTile(paper, index, { compact: true });

            if (isLastOddTile) {
              return (
                <div key={paper.slug} className="col-span-2 flex justify-center">
                  <div className="w-full max-w-[calc((100%-0.5rem)/2)]">{tile}</div>
                </div>
              );
            }

            return tile;
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col w-full px-4 md:px-8 py-4 md:py-6">
      <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 shrink-0">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-ink font-montserrat">
            {t('navigation.takeTest')}
          </h1>
          <p className="text-ink-muted mt-2 text-sm md:text-base text-justify max-w-2xl">
            {t('test.paperMenuSubtitle')}
          </p>
        </div>

        <div
          ref={progressRef}
          className="flex flex-wrap items-center gap-3 lg:justify-end"
          data-guide-anchor="progress"
        >
          <div className="flex items-center gap-3 min-w-[200px]">
            <span className="text-sm font-medium text-ink-muted whitespace-nowrap">
              {t('test.abilitiesProgress', { completed, total: CORE_ABILITY_COUNT })}
            </span>
            <div className={testProgressTrack}>
              <div className={testProgressFill} style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
          <TutorialInlineButton onClick={openGuide} />
        </div>
      </header>

      <div
        ref={gridRef}
        data-guide-anchor="grid"
        className="grid w-full flex-1 grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 items-stretch"
        style={{ gridAutoRows: UNIFORM_GRID_ROW_HEIGHT }}
      >
        {papers.map((paper, index) => {
          const isLastTile = index === papers.length - 1;
          const isSecondRowDesktop = index >= 3;
          const tileRef = index === 0 ? firstTileRef : undefined;

          let cellClass = 'h-full min-h-0 min-w-0 md:col-span-2';
          if (isSecondRowDesktop && !isLastTile) {
            cellClass += ' md:col-start-2';
          }
          if (isLastTile) {
            cellClass += ' col-span-2 flex justify-center md:col-start-4 md:flex-none';
          }

          const tile = renderPaperTile(paper, index, { tileRef });

          if (isLastTile) {
            return (
              <div key={paper.slug} className={cellClass}>
                <div className="w-1/2 md:w-full h-full min-h-0">{tile}</div>
              </div>
            );
          }

          return (
            <div key={paper.slug} className={cellClass}>
              {tile}
            </div>
          );
        })}
      </div>

      {completed < CORE_ABILITY_COUNT ? (
        <p className="mt-6 text-center text-sm text-ink-muted shrink-0">
          {t('test.abilitiesProgressHint', { count: CORE_ABILITY_COUNT - completed })}
        </p>
      ) : (
        <p className="mt-6 text-center text-sm font-semibold text-link shrink-0">
          {t('test.allAbilitiesComplete')}
        </p>
      )}

      <AlgorithmResultsDisclaimer className="mt-6 shrink-0" />

      <PageTutorialGuide
        active={guideOpen}
        onClose={closeGuide}
        t={t}
        steps={guideSteps}
        anchorRefs={anchorRefs}
      />
    </div>
  );
}

function AbilityPaperTile({
  tileRef,
  index,
  infographicVariant,
  title,
  isComplete,
  isSoon,
  isInProgress,
  answered = 0,
  questionTotal = 0,
  partsCompleted = 0,
  partsTotal = 5,
  hideIdleBadge = false,
  testFocusRing,
  t,
  onOpenPart,
  compact = false,
}) {
  const theme = getAbilityTheme(infographicVariant);

  const badgeLabel = (() => {
    if (hideIdleBadge && !isComplete && !isInProgress && !isSoon) return null;
    if (isComplete) {
      return (
        <span className="inline-flex items-center gap-1">
          <span aria-hidden>✓</span> {t('test.chapterComplete')}
        </span>
      );
    }
    if (isSoon) return t('test.comingSoon');
    if (isInProgress) {
      return t('test.partQuestionProgress', { answered, total: questionTotal });
    }
    return t('test.partsProgress', { completed: partsCompleted, total: partsTotal });
  })();

  const handleOpen = () => {
    if (!isSoon) onOpenPart?.();
  };

  const handleTitleClick = (event) => {
    event.stopPropagation();
    handleOpen();
  };

  const graphicMinHeight = compact ? 'min-h-[72px]' : 'min-h-[140px] md:min-h-[160px]';

  return (
    <article
      ref={tileRef}
      data-guide-anchor={index === 0 ? 'first-tile' : undefined}
      className={`group relative flex h-full w-full min-w-0 min-h-0 flex-col rounded-lg overflow-hidden bg-surface border border-edge shadow-sm hover:shadow-md ${theme.hoverBorder} transition-[box-shadow,border-color] duration-500 ease-out ${compact ? 'text-center' : 'text-left'}`}
    >
      <div
        role="button"
        tabIndex={isSoon ? -1 : 0}
        aria-label={title}
        onClick={(event) => {
          event.stopPropagation();
          handleOpen();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
          }
        }}
        className={`relative w-full min-h-0 flex-1 overflow-hidden ${graphicMinHeight} ${testFocusRing} ${
          isSoon ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
        }`}
        style={{ backgroundColor: theme.tileBg }}
      >
        <div
          className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-transform duration-500 ease-out group-hover:scale-105 group-active:scale-[1.04] ${
            compact ? 'p-1.5' : 'p-4'
          }`}
        >
          <AbilityInfographic
            variant={infographicVariant}
            theme={theme}
            compact={compact}
          />
        </div>
        {badgeLabel ? (
          <span className="absolute top-2 right-2 z-10 text-xs font-bold text-ink bg-surface/85 px-2 py-1 rounded-md pointer-events-none">
            {badgeLabel}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={handleTitleClick}
        disabled={isSoon}
        className={`shrink-0 bg-surface border-t border-edge w-full transition-colors duration-500 ease-out ${
          compact ? 'px-2 py-2 text-center' : 'px-3 py-3 text-left'
        } ${testFocusRing} ${
          isSoon ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-surface-muted'
        }`}
      >
        <p
          className={`font-bold leading-normal text-center ${theme.titleClass} ${
            compact ? 'text-xs' : 'text-sm md:text-base'
          }`}
        >
          {title}
        </p>
      </button>
    </article>
  );
}

export default AbilityPaperMenu;
